import clsx from 'clsx';

import { useTableContext } from '@core/table/features/TableContext';
import type { Column } from '@core/table/types';
import Typography from '@core/Typography';

import SortButton from './SortButton';
import getTableHeadCellCss, { classes } from './styles/getTableHeadCellCss';

export type Props<
  Row extends Record<string, any>,
  K extends keyof Row & string
> = Omit<Column<Row, K>, 'renderCell' | 'sortComparator'>;

const TableHeadCell = <
  Row extends Record<string, any>,
  K extends keyof Row & string
>(
  props: Props<Row, K>
) => {
  const {
    label,
    justify,
    accessor,
    sortable,
    width,
    'aria-label': ariaLabel,
    ...rest
  } = props;

  const { sort, status } = useTableContext();

  const isSortingEnabled = sortable && status === 'idle';

  const componentToRender = isSortingEnabled ? (
    <SortButton {...{ label, sortable, accessor, 'aria-label': ariaLabel }} />
  ) : (
    <Typography aria-label={ariaLabel} color="supportText">
      <strong>{label}</strong>
    </Typography>
  );

  const ariaSort =
    sort?.key === accessor && isSortingEnabled ? sort?.direction : undefined;

  return (
    <th
      aria-label={ariaLabel}
      aria-sort={ariaSort}
      className={clsx({
        [classes.sorted]: isSortingEnabled && sort.key === accessor,
        [classes.sortIconEnd]: isSortingEnabled && justify !== 'end',
        [classes.sortIconStart]: isSortingEnabled && justify === 'end',
        [classes.endJustified]: justify === 'end',
      })}
      css={getTableHeadCellCss({ width })}
      role="columnheader"
      scope="col"
      {...rest}
    >
      {componentToRender}
    </th>
  );
};

export default TableHeadCell;
